import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ConsolidatedReport } from "../api/mentorship";

// Client-side PDF generation for the periodic mentorship consolidation
// report — mirrors the structure of the reviewed Rwanda Coding Academy
// reference document (basics -> meeting log -> student comments ->
// follow-up recommendation), reusing the same jsPDF/jspdf-autotable
// pipeline already proven in LessonReportRollupService.ts/SchemeReportService.ts.

export interface MentorshipReportMetadata {
  schoolName: string;
  academicYearName: string;
  generatedBy: string;
}

// Static boilerplate mirroring the reviewed Rwanda Coding Academy reference
// report's "Objectives of mentoring assignment" section — the same three
// categories, verbatim in intent. Per the plan (§5.2), this stays static
// for v1; making it admin-editable is a plausible future iteration, not a
// current requirement.
const CATEGORY_LABEL: Record<string, string> = {
  APPRECIATION: "Appreciation",
  CONCERN: "Concern",
  REQUEST_MEETING: "Meeting Request",
  GENERAL: "General",
};

const OBJECTIVES: { heading: string; items: string[] }[] = [
  {
    heading: "Personal Development",
    items: [
      "Help students develop self-confidence and self-awareness.",
      "Encourage goal-setting and self-reflection.",
    ],
  },
  {
    heading: "Career Exploration",
    items: [
      "Introduce students to different career paths and opportunities.",
      "Facilitate discussions about interests and aspirations related to future careers.",
    ],
  },
  {
    heading: "Life Skills Development",
    items: [
      "Teach valuable life skills such as time management, communication, and problem-solving.",
      "Prepare students for responsibilities such as college applications and financial literacy.",
    ],
  },
];

export const MentorshipReportService = {
  buildReport: (report: ConsolidatedReport, metadata: MentorshipReportMetadata) => {
    const doc = new jsPDF("p", "mm", "a4");
    const BLACK: [number, number, number] = [0, 0, 0];
    const GRAY: [number, number, number] = [100, 100, 100];
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - 30;

    // ── Cover page ──────────────────────────────────────────────────────
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...BLACK);
    doc.text("Mentors' Progressive Report", 15, 20);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...GRAY);
    doc.text(
      `Mentor: ${report.mentor_name}  ·  ${metadata.academicYearName}  ·  ${metadata.schoolName}`,
      15,
      28,
    );
    doc.text(`Mentees: ${report.mentees.length}`, 15, 34);
    doc.setFontSize(9);
    doc.text(`Generated by ${metadata.generatedBy}`, 15, 40);

    let coverY = 52;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...BLACK);
    doc.text("Overview", 15, coverY);
    coverY += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...GRAY);
    const overviewText = doc.splitTextToSize(
      `The academic mentorship relationship between ${report.mentor_name} and ${report.mentees.length} ` +
        `mentee${report.mentees.length !== 1 ? "s" : ""} named in this report has been structured to foster ` +
        `personal and professional development within the academic environment. This collaboration focuses on ` +
        `guiding each mentee through their educational journey, enhancing knowledge acquisition, skill development, ` +
        `and overall academic success.`,
      contentWidth,
    );
    doc.text(overviewText, 15, coverY);
    coverY += overviewText.length * 4.2 + 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...BLACK);
    doc.text("Objectives of Mentoring Assignment", 15, coverY);
    coverY += 7;

    for (const section of OBJECTIVES) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...BLACK);
      doc.text(section.heading, 15, coverY);
      coverY += 5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...GRAY);
      for (const item of section.items) {
        const lines = doc.splitTextToSize(`•  ${item}`, contentWidth - 4);
        doc.text(lines, 18, coverY);
        coverY += lines.length * 4.2 + 1.5;
      }
      coverY += 3;
    }

    for (const mentee of report.mentees) {
      doc.addPage();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(...BLACK);
      doc.text(mentee.name, 15, 18);

      // Basics table
      autoTable(doc, {
        startY: 24,
        head: [["Date of Birth", "Latest Scores", "Recommend Follow-up"]],
        body: [
          [
            mentee.date_of_birth ?? "—",
            mentee.scores.length > 0
              ? mentee.scores
                  .slice(0, 5)
                  .map((s) => `${s.subject_name ?? ""}: ${s.score}/${s.max_score}`)
                  .join(" · ")
              : "No scores on file",
            mentee.recommend_follow_up ? "Yes" : "No",
          ],
        ],
        theme: "grid",
        styles: { fontSize: 8 },
        headStyles: { fillColor: [219, 196, 162], textColor: BLACK },
      });

      // Meeting schedule log
      const afterBasicsY = (doc as any).lastAutoTable.finalY + 6;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Mentoring Plan Meeting Schedule", 15, afterBasicsY);

      autoTable(doc, {
        startY: afterBasicsY + 3,
        head: [["Date", "Content of Discussion", "Recommendations", "Mentor's Remarks"]],
        body:
          mentee.sessions.length > 0
            ? mentee.sessions.map((s) => [
                s.session_date ?? "—",
                s.topic ?? "—",
                s.next_steps ?? s.guidance_notes ?? "—",
                s.notes ?? "—",
              ])
            : [["—", "No sessions recorded in this period", "—", "—"]],
        theme: "grid",
        styles: { fontSize: 8, cellWidth: "wrap" },
        headStyles: { fillColor: [219, 196, 162], textColor: BLACK },
      });

      // Student comment / appreciation
      const afterSessionsY = (doc as any).lastAutoTable.finalY + 6;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Student Comment and Appreciation", 15, afterSessionsY);

      autoTable(doc, {
        startY: afterSessionsY + 3,
        head: [["Date", "Category", "Message"]],
        body:
          mentee.comments.length > 0
            ? mentee.comments.map((c) => [c.submitted_at ?? "—", CATEGORY_LABEL[c.category] ?? c.category, c.message])
            : [["—", "—", "Not yet submitted by the mentee"]],
        theme: "grid",
        styles: { fontSize: 8, cellWidth: "wrap" },
        headStyles: { fillColor: [219, 196, 162], textColor: BLACK },
      });

      const finalY = (doc as any).lastAutoTable.finalY + 10;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...GRAY);
      doc.text(
        `Recommendation: ${
          mentee.recommend_follow_up
            ? "Recommend this student for special follow-up."
            : "No special follow-up recommended at this time."
        }`,
        15,
        finalY,
      );
    }

    // ── Closing page — mirrors the reference report's "Future Directions",
    // "Conclusion", and "Prepared and signed by" sections ────────────────
    doc.addPage();
    let closingY = 20;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...BLACK);
    doc.text("Future Directions on this Report", 15, closingY);
    closingY += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...GRAY);
    const futureText = doc.splitTextToSize(
      "In order to keep each mentee on a road of success and progress, the mentorship relationship will " +
        "continue to focus on future projects, further skill development, and exposure to new approaches.",
      contentWidth,
    );
    doc.text(futureText, 15, closingY);
    closingY += futureText.length * 4.2 + 10;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...BLACK);
    doc.text("Conclusion", 15, closingY);
    closingY += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...GRAY);
    const conclusionText = doc.splitTextToSize(
      `${report.mentees.length > 0 ? "Each mentee has" : "Mentees have"} benefited from the mentorship ` +
        "experience, achieving both academic success and personal growth. In order to ensure continued " +
        "advancement, future sessions will seek to build upon this foundation.",
      contentWidth,
    );
    doc.text(conclusionText, 15, closingY);
    closingY += conclusionText.length * 4.2 + 16;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...BLACK);
    doc.text("Prepared and signed by", 15, closingY);
    closingY += 6;
    doc.setFont("helvetica", "bold");
    doc.text(metadata.generatedBy, 15, closingY);
    closingY += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...GRAY);
    doc.text(`On ${new Date().toISOString().slice(0, 10)}`, 15, closingY);

    return doc;
  },

  download: (report: ConsolidatedReport, metadata: MentorshipReportMetadata) => {
    const doc = MentorshipReportService.buildReport(report, metadata);
    doc.save(`mentorship-report-${report.mentor_name.replace(/\s+/g, "-")}.pdf`);
  },
};
